import React, { useEffect, useState, useRef } from 'react';
import { Record } from 'immutable';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useMeetingModeTracking } from 'Shared/tracking/use-meeting-mode-tracking';
import { useMeetingBot } from 'Shared/utils/hooks/use-meeting-bot';

// Types
import { WorkspaceMeetingModeState, WorkspaceMeetingMode } from 'Shared/types/WorkspaceMeetingMode.type';

// Utils
import { startShimmerBuildup } from 'Shared/components/LoadingShimmer/shimmer-util';

// Constants
import WorkspaceMeetingModeConstants from 'Shared/constants/WorkspaceMeetingMode.constants';

// Actions
import * as WorkspaceMeetingModeActions from 'Shared/actions/WorkspaceMeetingMode.actions';

// API
import { SummaryResponse } from 'Shared/api/BotManagement.api';

// Components
import { WpContainer, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import OtherActionItems from './OtherActionItem/OtherActionItems';
import CreatedInMeetingMode from './CreatedInMeetingMode/CreatedInMeetingMode';
import { WpText } from 'Wpds/components/text';
import { WpBadge } from 'Wpds/components/badge';
import SuggestedActionItems from './SuggestedActionItems/SuggestedActionItems';
import LoadingShimmer from 'Shared/components/LoadingShimmer/LoadingShimmer';

// Styles
import './NextSteps.scss';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

type Props = {
  meetingModeSession?: Record<WorkspaceMeetingMode>;
  handleNavigateToItem: Function;
  meetingItemOrderedKeys: Array<string>;
};

const MAX_SHIMMER_START_POINT = 1000;
const SHIMMER_STEP = 100; // px increment for each new shimmer
const SHIMMER_INTERVAL = 800; // ms between each shimmer appearing on loading

const NextSteps = ({ meetingModeSession, handleNavigateToItem, meetingItemOrderedKeys }: Props) => {
  const dispatch = useDispatch();
  const { trackPreviousClick } = useMeetingModeTracking();
  const { workspaceId } = useParams();
  const { fetchMeetingSummary, bot, meetingBotIsInProgress, remove: removeBot, isMeetingBotEnabled } = useMeetingBot();

  const [isEnding, toggleIsEnding] = useToggle(false);
  const [isNavigatingBack, toggleIsNavigatingBack] = useToggle(false);
  const [meetingSummary, setMeetingSummary] = useState<SummaryResponse>(null);
  const [shimmerStarts, setShimmerStarts] = useState<string[]>(['0px']);
  const shimmerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleNavigateToPreviousClick = () => {
    toggleIsNavigatingBack();
    trackPreviousClick();
    handleNavigateToItem(meetingItemOrderedKeys[meetingItemOrderedKeys.length - 1], 'back');
  };

  const handleEndMeetingClick = async () => {
    toggleIsEnding();
    if (bot && !bot.isComplete) {
      await removeBot(bot.recallBotId);
    }
    const params = {
      [WorkspaceMeetingModeConstants.Params.STATE]: WorkspaceMeetingModeState.END_MEETING_MODE_SESSION,
    };
    dispatch(WorkspaceMeetingModeActions.updateMeetingModeV2(workspaceId, params));
  };

  const handleMeetingSummaryRefreshClick = async () => {
    setMeetingSummary(null); // Reset summary to trigger loading state
    const refreshedSummary = await fetchMeetingSummary(true);
    setMeetingSummary(refreshedSummary);
  };

  useEffect(() => {
    if (bot && !meetingSummary) {
      (async () => {
        const summary = await fetchMeetingSummary();
        setMeetingSummary(summary);
      })();
    }
  }, []);

  useEffect(() => {
    if (!meetingSummary) {
      return startShimmerBuildup({
        setShimmerStarts,
        shimmerStep: SHIMMER_STEP,
        maxShimmerStartPoint: MAX_SHIMMER_START_POINT,
        initialInterval: SHIMMER_INTERVAL,
        minInterval: 500,
        maxInterval: 1200,
        onCleanup: timeoutId => {
          shimmerIntervalRef.current = timeoutId;
        },
      });
    } else {
      // Stop shimmer buildup when summary is loaded
      if (shimmerIntervalRef.current) clearTimeout(shimmerIntervalRef.current);
      setShimmerStarts(['0px']);
    }
  }, [meetingSummary]);

  const baseClassName = 'NextSteps';
  return (
    <div className={baseClassName}>
      <WpContainer className={`${baseClassName}__wrap-up-container`}>
        <WpContainerBody>
          {isMeetingBotEnabled && meetingBotIsInProgress ? (
            <>
              <div className={`${baseClassName}__wrap-up-container__meeting-summary-title`}>
                <WpText variant="bodyMd" bold>
                  Meeting Summary
                </WpText>
                <div className={`${baseClassName}__wrap-up-container__meeting-summary-actions`}>
                  <WpTooltip>
                    <WpTooltipTrigger>
                      <WpButton
                        variant="tertiary"
                        onClick={handleMeetingSummaryRefreshClick}
                        disabled={!meetingSummary}
                      >
                        <WpIcon name="sync" />
                      </WpButton>
                    </WpTooltipTrigger>
                    <WpTooltipContent>Regenerate meeting summary</WpTooltipContent>
                  </WpTooltip>
                  <WpBadge color="blue" size="md">
                    Beta
                  </WpBadge>
                </div>
              </div>
              <div className={`${baseClassName}__wrap-up-container__summary`}>
                {meetingSummary ? (
                  <>
                    <WpText variant="bodyMd">{meetingSummary.data.general_summary}</WpText>

                    {meetingSummary.data.decisions.length > 0 && (
                      <>
                        <WpText variant="bodyMd" bold>
                          Decisions:
                        </WpText>
                        <ul>
                          {meetingSummary.data.decisions.map(item => (
                            <li key={item}>
                              <WpText variant="bodySm">{item}</WpText>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {meetingSummary.data.key_takeaways.length > 0 && (
                      <>
                        <WpText variant="bodyMd" bold>
                          Key Takeaways:
                        </WpText>
                        <ul>
                          {meetingSummary.data.key_takeaways.map(item => (
                            <li key={item}>
                              <WpText variant="bodySm">{item}</WpText>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <WpText variant="bodyMd" italic>
                      Sit tight, generating your Meeting Summary...
                    </WpText>
                    {shimmerStarts.map(start => {
                      // Used to determine if there should be a "paragraph" gap
                      const isGap = (shimmerStarts.indexOf(start) + 1) % 4 === 0;
                      // Generate a random width percentage between 85% and 100%
                      // Using this formula to create a dynamic width that keeps track with previous
                      const widthPercent = 85 + (Math.abs(Math.sin(shimmerStarts.indexOf(start) * 17 + 7) * 1000) % 11);
                      return (
                        <React.Fragment key={start}>
                          {isGap && <div className={`${baseClassName}__wrap-up-container__paragraph-gap`}></div>}
                          <div className="mb-2">
                            <LoadingShimmer
                              shape="rectangle"
                              height="16px"
                              width={`${widthPercent}%`}
                              shimmerStart={start}
                            />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div className={`${baseClassName}__wrap-up-container__sub-title`}>Wrap Up</div>
              <div className={`${baseClassName}__wrap-up-container__title`}>Review Action Items</div>
            </>
          )}
        </WpContainerBody>
        <WpContainerFooter justifyContent="spaceBetween">
          <WpButton
            variant="tertiary"
            disabled={isEnding}
            loading={isNavigatingBack}
            onClick={handleNavigateToPreviousClick}
          >
            <WpIcon name="angle-left" />
            Previous Topic
          </WpButton>
          <WpButton onClick={handleEndMeetingClick} loading={isEnding}>
            End Discussion
          </WpButton>
        </WpContainerFooter>
      </WpContainer>
      <div className={`${baseClassName}__created-in-items`}>
        <CreatedInMeetingMode meetingModeSession={meetingModeSession} />
      </div>
      {isMeetingBotEnabled && meetingBotIsInProgress && (
        <div className={`${baseClassName}__suggested-items`}>
          <SuggestedActionItems meetingSummary={meetingSummary} fetchingMeetingSummary={!meetingSummary} />
        </div>
      )}
      <div className={`${baseClassName}__other-items`}>
        <OtherActionItems meetingModeSession={meetingModeSession} />
      </div>
    </div>
  );
};

export default NextSteps;
